// ipad-pwa/src/lib/auth.js
// =============================================================================
// In-memory cache of the device_token + HMAC signing secret. Persisted as
// an AES-GCM-sealed blob in localStorage. On every cold start we call
// /api/devices/:id/handshake to retrieve `wrap_key_half_b` from the server,
// XOR it with the iPad-side `wrap_key_half_a` to derive the AES key, and
// decrypt the blob into memory only. The cleartext never touches disk on
// the iPad after the initial pair.
//
// THREE OPERATING MODES at runtime, chosen automatically by bootstrap():
//
//   1. sealed   — Phase 2 paired device. Sealed bundle exists, handshake
//                 succeeds, in-memory token + secret available, HMAC
//                 attaches on mutations.
//
//   2. legacy   — Pre-Phase-2 paired device (or a Phase-2 device whose
//                 handshake is failing — e.g. nginx mTLS misconfig). Falls
//                 back to the cleartext device_token from the old `device`
//                 storage key. No HMAC. Server soft-mode allows the
//                 request without X-Body-Signature.
//
//   3. unpaired — No device record at all. App shows the pairing screen.
// =============================================================================

import {
  getDevice,
  setDevice,
  clearDevice,
  getSealedBundle,
  setSealedBundle,
  clearSealedBundle,
  hydrateCache,
} from './storage.js';
import { unsealBundle, sealBundle, b64ToBuf } from './crypto.js';

let _mode = 'unpaired';            // 'sealed' | 'legacy' | 'unpaired'
let _deviceToken = null;           // string
let _signingSecretBytes = null;    // Uint8Array (32) — null in legacy / unpaired
let _device = null;                // { id, name, location }

export function getMode() { return _mode; }
export function getDeviceToken() { return _deviceToken; }
export function getSigningSecretBytes() { return _signingSecretBytes; }
export function getDeviceRecord() { return _device; }

/** Clear everything (used by an explicit "unpair"). */
export function logout() {
  _mode = 'unpaired';
  _deviceToken = null;
  _signingSecretBytes = null;
  _device = null;
  clearSealedBundle();
  clearDevice();
}

/**
 * Boot-time setup. Tries the sealed flow first, falls back to legacy.
 * Returns the resolved mode. Safe to call multiple times — re-handshakes
 * only when we have a sealed bundle and haven't already unsealed it.
 */
export async function bootstrap() {
  if (_mode !== 'unpaired' && _deviceToken) return _mode;

  // IDB-backed storage cache must be hydrated before any sync read
  // returns a meaningful value. Idempotent — safe to call repeatedly.
  await hydrateCache();

  const sealed = getSealedBundle();
  if (sealed && sealed.device_id && sealed.half_a_b64 && sealed.ciphertext_b64) {
    try {
      const halfA = b64ToBuf(sealed.half_a_b64);
      // Call /handshake. During Phase 1 the server's soft path accepts a
      // device_token in the body; once nginx mTLS goes live the iPad's
      // installed client cert authenticates the handshake instead.
      const r = await fetch(`/api/devices/${encodeURIComponent(sealed.device_id)}/handshake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_token: _legacyFallbackTokenFromDisk() }),
      });
      if (!r.ok) throw new Error(`handshake HTTP ${r.status}`);
      const { wrap_key_half_b } = await r.json();
      const halfB = b64ToBuf(wrap_key_half_b);
      const plaintext = await unsealBundle(
        sealed.ciphertext_b64,
        sealed.iv_b64,
        halfA,
        halfB,
      );
      _deviceToken = plaintext.device_token;
      _signingSecretBytes = b64ToBuf(plaintext.signing_secret_b64);
      _device = sealed.device || null;
      _mode = 'sealed';
      halfB.fill(0);
      return _mode;
    } catch (err) {
      // Sealed bootstrap failed (nginx mTLS not yet live, or cert
      // missing, or network down). Fall through to legacy if available.
      console.warn('[auth] sealed bootstrap failed, falling back to legacy:', err);
    }
  }

  const legacy = getDevice();
  if (legacy && legacy.token) {
    _deviceToken = legacy.token;
    _signingSecretBytes = null;
    _device = legacy.device || null;
    _mode = 'legacy';
    return _mode;
  }

  // Last-resort recovery: the iPad has no sealed bundle and no legacy
  // device record (iOS wiped IndexedDB / website data), but the Home
  // Screen URL still carries the 5-char kiosk token as a path segment
  // (/v3/<TOKEN>). Exchange it for a fresh device_token + sealed bundle,
  // so the operator never sees the pairing screen unless the admin has
  // disabled or regenerated the kiosk URL.
  const kioskToken = _readKioskTokenFromUrl();
  if (kioskToken) {
    try {
      const r = await fetch('/api/devices/kiosk-bootstrap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kiosk_token: kioskToken }),
      });
      if (r.ok) {
        const payload = await r.json();
        return await adoptPairResponse(payload);
      }
      console.warn('[auth] kiosk-bootstrap HTTP', r.status);
    } catch (err) {
      console.warn('[auth] kiosk-bootstrap failed:', err);
    }
  }

  _mode = 'unpaired';
  return _mode;
}

function _readKioskTokenFromUrl() {
  const RE = /^[A-HJ-NP-Z2-9]{5}$/;
  try {
    // Primary: token as the /v3/<TOKEN> path segment (the Jamf Web Clip
    // URL format). e.g. /v3/ABCDE  ->  ABCDE
    const m = window.location.pathname.match(/\/v3\/([A-Za-z0-9]{5})(?:[/?#]|$)/);
    if (m) {
      const t = m[1].toUpperCase();
      if (RE.test(t)) return t;
    }
    // Fallback: ?t=<TOKEN> query string, for manually-constructed URLs.
    const q = (new URLSearchParams(window.location.search).get('t') || '').toUpperCase();
    if (RE.test(q)) return q;
    return null;
  } catch {
    return null;
  }
}

function _legacyFallbackTokenFromDisk() {
  const d = getDevice();
  return d?.token || '';
}

/**
 * Called by PairingScreen on a successful POST /api/devices/pair.
 * If the server payload contains wrap_key_half_a + signing_secret
 * (Phase 1+ server), we run the handshake straight away, seal the
 * secrets, and drop the cleartext from storage. Older servers fall
 * back to the legacy plaintext path.
 */
export async function adoptPairResponse(r) {
  const { device_token, device } = r;
  const halfA_b64 = r.wrap_key_half_a;
  const signingSecret_b64 = r.signing_secret;

  if (!halfA_b64 || !signingSecret_b64) {
    setDevice({ token: device_token, device });
    _mode = 'legacy';
    _deviceToken = device_token;
    _signingSecretBytes = null;
    _device = device;
    return _mode;
  }

  const halfA = b64ToBuf(halfA_b64);
  const hr = await fetch(`/api/devices/${encodeURIComponent(device.id)}/handshake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_token }),
  });
  if (!hr.ok) throw new Error('Could not complete handshake during pair');
  const { wrap_key_half_b } = await hr.json();
  const halfB = b64ToBuf(wrap_key_half_b);

  const { iv_b64, ciphertext_b64 } = await sealBundle(
    { device_token, signing_secret_b64: signingSecret_b64 },
    halfA,
    halfB,
  );

  setSealedBundle({
    device_id: device.id,
    half_a_b64: halfA_b64,
    iv_b64,
    ciphertext_b64,
    device,
  });
  // KEEP the legacy device record alongside the sealed bundle while
  // nginx is still in soft-mTLS mode. The /handshake route only has
  // two ways to authenticate the iPad:
  //   (a) a verified client certificate (Phase 3 — nginx must enforce
  //       ssl_verify_client; not yet wired), OR
  //   (b) a device_token in the request body (Phase 2 fallback).
  // Path (b) is what every iPad uses today, and without the legacy
  // record on disk the cold-start handshake has nothing to send →
  // 401 → app wipes the sealed bundle and demands re-pairing on
  // every restart. Once Phase 3 enables mTLS enforcement, we can
  // safely call clearDevice() here and the body-token fallback path
  // can be removed from the server.
  setDevice({ token: device_token, device });

  _mode = 'sealed';
  _deviceToken = device_token;
  _signingSecretBytes = b64ToBuf(signingSecret_b64);
  _device = device;
  halfB.fill(0);
  return _mode;
}
